import axios from 'axios'
import { getRockefellerApiOptions } from 'store/shared'
import { call, cancelled, put, takeLatest } from 'typed-redux-saga'
import {
  fetchChifamDetailsService,
  fetchChifamHoldingsService,
  fetchChifamHoldingSummaryService,
  fetchAccountsService,
  insertUpdateReallocationService,
  generateDocumentService,
  fetchChifamAccountsService
} from '../api/services'
import {
  fetchChifamDetailsActions,
  fetchChifamHoldingDetailsActions,
  fetchChifamHoldingSummaryActions,
  fetchAccountsActions,
  insertUpdateReallocationActions,
  generateDocumentActions,
  fetchChifamAccountsActions
} from './actions'

function* handleChifamDetailsActions(
  action: ReturnType<typeof fetchChifamDetailsActions.request>
) {
  // eslint-disable-next-line import/no-named-as-default-member
  const source = axios.CancelToken.source()
  const apiOptions = yield* call(getRockefellerApiOptions, source.token)

  try {
    const result = yield* call(
      fetchChifamDetailsService,
      action.payload,
      apiOptions
    )
    yield put(fetchChifamDetailsActions.success(result))
  } catch (e: any) {
    yield put(fetchChifamDetailsActions.failure(e))
  } finally {
    if (yield* cancelled()) {
      source.cancel()
    }
  }
}

function* handleChifamHoldingDetailsActions(
  action: ReturnType<typeof fetchChifamHoldingDetailsActions.request>
) {
  // eslint-disable-next-line import/no-named-as-default-member
  const source = axios.CancelToken.source()
  const apiOptions = yield* call(getRockefellerApiOptions, source.token)

  try {
    const result = yield* call(
      fetchChifamHoldingsService,
      action.payload,
      apiOptions
    )

    yield put(fetchChifamHoldingDetailsActions.success(result))
  } catch (e: any) {
    yield put(fetchChifamHoldingDetailsActions.failure(e))
  } finally {
    if (yield* cancelled()) {
      source.cancel()
    }
  }
}

function* handleChifamHoldingSummaryActions(
  action: ReturnType<typeof fetchChifamHoldingSummaryActions.request>
) {
  // eslint-disable-next-line import/no-named-as-default-member
  const source = axios.CancelToken.source()
  const apiOptions = yield* call(getRockefellerApiOptions, source.token)

  try {
    const result = yield* call(
      fetchChifamHoldingSummaryService,
      action.payload,
      apiOptions
    )
    yield put(fetchChifamHoldingSummaryActions.success(result))
  } catch (e: any) {
    yield put(fetchChifamHoldingSummaryActions.failure(e))
  } finally {
    if (yield* cancelled()) {
      source.cancel()
    }
  }
}

function* handleFetchAccountsActions(
  action: ReturnType<typeof fetchAccountsActions.request>
) {
  // eslint-disable-next-line import/no-named-as-default-member
  const source = axios.CancelToken.source()
  const apiOptions = yield* call(getRockefellerApiOptions, source.token)

  try {
    const result = yield* call(fetchAccountsService, action.payload, apiOptions)
    yield put(fetchAccountsActions.success(result))
  } catch (e: any) {
    yield put(fetchAccountsActions.failure(e))
  } finally {
    if (yield* cancelled()) {
      source.cancel()
    }
  }
}

function* handleInsertUpdateReallocationActions(
  action: ReturnType<typeof insertUpdateReallocationActions.request>
) {
  // eslint-disable-next-line import/no-named-as-default-member
  const source = axios.CancelToken.source()
  const apiOptions = yield* call(getRockefellerApiOptions, source.token)

  try {
    const result = yield* call(
      insertUpdateReallocationService,
      action.payload,
      apiOptions
    )
    yield put(insertUpdateReallocationActions.success(result))
  } catch (e: any) {
    console.error('Insert/Update Reallocation Error:', e)
    yield put(insertUpdateReallocationActions.failure(e))
  } finally {
    if (yield* cancelled()) {
      source.cancel()
    }
  }
}

function* handleGenerateDocumentActions(
  action: ReturnType<typeof generateDocumentActions.request>
) {
  // eslint-disable-next-line import/no-named-as-default-member
  const source = axios.CancelToken.source()
  const apiOptions = yield* call(getRockefellerApiOptions, source.token)

  try {
    const result = yield* call(
      generateDocumentService,
      action.payload,
      apiOptions
    )
    yield put(generateDocumentActions.success(result))
  } catch (e: any) {
    console.error('Generate Document Error:', e)

    // Handle axios error responses with JSON body (e.g., 500 status with error details)
    if (e.response?.data) {
      const blob = e.response.data
      if (blob instanceof Blob && blob.type === 'application/json') {
        try {
          const text = yield* call([blob, blob.text])
          const errorResponse = JSON.parse(text)
          const error: any = new Error(
            errorResponse.message || 'Failed to generate document'
          )
          error.errorCode = errorResponse.errorCode
          error.message = errorResponse.message
          yield put(generateDocumentActions.failure(error))
          return
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError)
        }
      }
    }

    yield put(generateDocumentActions.failure(e))
  } finally {
    if (yield* cancelled()) {
      source.cancel()
    }
  }
}

function* handleFetchChifamAccountsActions(
  action: ReturnType<typeof fetchChifamAccountsActions.request>
) {
  // eslint-disable-next-line import/no-named-as-default-member
  const source = axios.CancelToken.source()
  const apiOptions = yield* call(getRockefellerApiOptions, source.token)

  try {
    const result = yield* call(
      fetchChifamAccountsService,
      action.payload,
      apiOptions
    )
    yield put(fetchChifamAccountsActions.success(result))
  } catch (e: any) {
    yield put(fetchChifamAccountsActions.failure(e))
  } finally {
    if (yield* cancelled()) {
      source.cancel()
    }
  }
}

export const chifamSagas = [
  () =>
    takeLatest(fetchChifamDetailsActions.request, handleChifamDetailsActions),
  () =>
    takeLatest(
      fetchChifamHoldingDetailsActions.request,
      handleChifamHoldingDetailsActions
    ),
  () =>
    takeLatest(
      fetchChifamHoldingSummaryActions.request,
      handleChifamHoldingSummaryActions
    ),
  () => takeLatest(fetchAccountsActions.request, handleFetchAccountsActions),
  () =>
    takeLatest(
      insertUpdateReallocationActions.request,
      handleInsertUpdateReallocationActions
    ),
  () =>
    takeLatest(generateDocumentActions.request, handleGenerateDocumentActions),
  () =>
    takeLatest(
      fetchChifamAccountsActions.request,
      handleFetchChifamAccountsActions
    )
]
